import React, { Suspense, lazy, useState } from 'react';
import PropTypes from 'prop-types';
import EmbedFeature from './embedFeature';

const AppConfiguration = lazy(() => import('../../../app-setting/AppConfiguration'));
const AiModelSection = lazy(() => import('../../../app-setting/AiModelSection'));
const CollectionDevApi = lazy(() => import('../../../app-setting/dev-key/CollectionDevApi'));
const ApiUsage = lazy(() => import('./ApiUsage'));
const EnvironmentSetting = lazy(() => import('../../../app-setting/EnvironmentSetting'));
const RobotsTxt = lazy(() => import('../../../app-setting/RobotsTxt'));
const SitemapXml = lazy(() => import('../../../app-setting/SitemapXml'));
const EncryptionSection = lazy(() => import('../../../app-setting/EncryptionSection'));
const DeletePermissions = lazy(() => import('../../../permission/DefaultPage'));
const ActivityLogs = lazy(() => import('../../../app-setting/ActivityLogs'));
const PublishTracklogs = lazy(() => import('../../../app-setting/PublishTracklogs'));

const SECTION_COMPONENTS = {
  'app-settings': AppConfiguration,
  'ai-models': AiModelSection,
  'developer-api': CollectionDevApi,
  'api-usage': ApiUsage,
  environments: EnvironmentSetting,
  encryption: EncryptionSection,
  permissions: DeletePermissions,
};

const SectionFallback = () => <div className="vibe-more-loading">Loading…</div>;

const MoreBack = ({ onBack }) => (
  <button type="button" className="vibe-more-back" onClick={onBack}>
    <i className="wb webconnect-arrow-left" aria-hidden="true" />
    <span>Sections</span>
  </button>
);

MoreBack.propTypes = {
  onBack: PropTypes.func.isRequired,
};

const Subnav = ({ tabs, active, onChange }) => (
  <div className="vibe-more-subnav" role="tablist">
    {tabs.map((tab) => (
      <button
        key={tab.id}
        type="button"
        role="tab"
        aria-selected={active === tab.id}
        className={`vibe-more-subnav-btn${active === tab.id ? ' is-active' : ''}`}
        onClick={() => onChange(tab.id)}
      >
        {tab.label}
      </button>
    ))}
  </div>
);

Subnav.propTypes = {
  tabs: PropTypes.array.isRequired,
  active: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
};

const SeoSection = ({ projectId, version }) => {
  const [tab, setTab] = useState('robots');
  const Feature = tab === 'sitemap' ? SitemapXml : RobotsTxt;
  return (
    <React.Fragment>
      <Subnav
        tabs={[
          { id: 'robots', label: 'Robots.txt' },
          { id: 'sitemap', label: 'Sitemap.xml' },
        ]}
        active={tab}
        onChange={setTab}
      />
      <Suspense fallback={<SectionFallback />}>
        <EmbedFeature component={Feature} projectId={projectId} version={version} />
      </Suspense>
    </React.Fragment>
  );
};

SeoSection.propTypes = {
  projectId: PropTypes.string,
  version: PropTypes.string,
};

const ActivitySection = ({ projectId, version }) => {
  const [tab, setTab] = useState('system');
  const Feature = tab === 'publish' ? PublishTracklogs : ActivityLogs;
  return (
    <React.Fragment>
      <Subnav
        tabs={[
          { id: 'system', label: 'System activity' },
          { id: 'publish', label: 'Publish activity' },
        ]}
        active={tab}
        onChange={setTab}
      />
      <Suspense fallback={<SectionFallback />}>
        <EmbedFeature component={Feature} projectId={projectId} version={version} />
      </Suspense>
    </React.Fragment>
  );
};

ActivitySection.propTypes = {
  projectId: PropTypes.string,
  version: PropTypes.string,
};

const MoreContent = ({ section, projectId, version, conversationId, showBack, onBack }) => {
  const Feature = SECTION_COMPONENTS[section];
  return (
    <div className="vibe-more-content">
      {showBack ? <MoreBack onBack={onBack} /> : null}
      {section === 'seo' ? <SeoSection projectId={projectId} version={version} /> : null}
      {section === 'activity' ? <ActivitySection projectId={projectId} version={version} /> : null}
      {Feature ? (
        <Suspense fallback={<SectionFallback />}>
          <EmbedFeature
            component={Feature}
            projectId={projectId}
            version={version}
            conversationId={conversationId}
          />
        </Suspense>
      ) : null}
    </div>
  );
};

MoreContent.propTypes = {
  section: PropTypes.string.isRequired,
  projectId: PropTypes.string,
  version: PropTypes.string,
  conversationId: PropTypes.string,
  showBack: PropTypes.bool,
  onBack: PropTypes.func.isRequired,
};

MoreContent.defaultProps = {
  projectId: '',
  version: '',
  conversationId: '',
  showBack: false,
};

export default MoreContent;
